import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, share, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import * as SharedActions from '@states/shared/shared.actions';
import { AppState } from '../app.state';
import { UtilsService } from '../../edge/utils.service';
import { PlaybackMonitoringActions } from '@states/playback-monitoring/playback-monitoring.action-types';
import { PlaybackMonitoringService } from '../../services/playback-monitoring.service';
import { PlaybackMonitoringModels } from '@models/playback-monitoring.models';
import PlaybackStatus = PlaybackMonitoringModels.PlaybackStatus;
import PlaybackProtocol = PlaybackMonitoringModels.PlaybackProtocol;
import { TranslateService } from '@ngx-translate/core';
import { getListForCleaningSuccess } from '@states/playback-monitoring/playback-monitoring.actions';

@Injectable()
export class PlaybackMonitoringEffects {


  public getLiveKitRoomList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlaybackMonitoringActions.getPlaybackMonitoring),
      switchMap(() => {
        return this.playbackMonitoringService
          .getPlaybackMonitoring()
          .pipe(
            switchMap(res => {
              const activePlaybacks = Object.keys(res.active ?? {});
              const cordonedPlaybacks = Object.keys(res.cordoned ?? {});
              const normalizedPlaybacks: PlaybackMonitoringModels.PlaybackListItem[] = [];
              let playbackDevices: PlaybackMonitoringModels.PlaybackDevice[] = [];

              activePlaybacks.forEach(serverName => {
                const playback = res.active[serverName];
                normalizedPlaybacks.push({ ...playback, status: PlaybackStatus.Active, serverName });
                playbackDevices = [...playbackDevices, ...this.generatePlaybackDevices(playback)];

              });
              cordonedPlaybacks.forEach(serverName => {
                const playback = res.cordoned[serverName];
                normalizedPlaybacks.push({ ...playback, status: PlaybackStatus.Cordoned, serverName });
                playbackDevices = [...playbackDevices, ...this.generatePlaybackDevices(playback)];
              });


              return [
                PlaybackMonitoringActions.getPlaybackMonitoringSuccess({
                  playbacks: normalizedPlaybacks,
                  playbackDevices,
                }),
                PlaybackMonitoringActions.setInitialLoader({ loading: false }),
              ];
            }),
            catchError(err => {
              const errMsg = this.utilsService.errMessage(err);
              return [
                SharedActions.consoleMessage({ error: errMsg }),
                PlaybackMonitoringActions.setInitialLoader({ loading: false }),
              ];
            }),
          );
      }),
      share(),
    ),
  );


  public deploy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlaybackMonitoringActions.deploy),
      switchMap(({ body }) => {
        return this.playbackMonitoringService
          .deploy(body)
          .pipe(
            switchMap(res => {
              return [
                PlaybackMonitoringActions.deploySuccess(),
                PlaybackMonitoringActions.setIsDeploying({ isDeploying: false }),
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.PLAYBACK_DEPLOYED_SUCCESS') }),
              ];
            }),
            catchError(err => {
              const errMsg = this.utilsService.errMessage(err);
              return [
                SharedActions.consoleMessage({ error: errMsg }),
                PlaybackMonitoringActions.setIsDeploying({ isDeploying: false }),
                PlaybackMonitoringActions.deployFail(),
                SharedActions.showMessage({ error: errMsg }),
              ];
            }),
          );
      }),
      share(),
    ),
  );


  public shutdown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlaybackMonitoringActions.shutdown),
      switchMap(({ vmId, cordon }) => {
        return this.playbackMonitoringService
          .setSoftCordon({ vmId, cordon })
          .pipe(
            switchMap(res => {
              return [
                PlaybackMonitoringActions.shutdownSuccess(),
                PlaybackMonitoringActions.stopShutdownLoading({ vmId }),
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.SHUTDOWN_SUCCESS') }),
              ];
            }),
            catchError(err => {
              const errMsg = this.utilsService.errMessage(err);
              return [
                SharedActions.consoleMessage({ error: errMsg }),
                PlaybackMonitoringActions.stopShutdownLoading({ vmId }),
                PlaybackMonitoringActions.shutdownFail(),
                SharedActions.showMessage({ error: errMsg }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public getListForCleaning$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlaybackMonitoringActions.getListForCleaning),
      switchMap(() => {
        return this.playbackMonitoringService
          .getListForCleaning()
          .pipe(
            switchMap(res => {
              const serverNames = Object.keys(res ?? {});

              const normalizedPlaybacks: PlaybackMonitoringModels.PlaybackListItem[] = [];

              serverNames.forEach(serverName => {
                const playback = res[serverName];
                normalizedPlaybacks.push({ ...playback, status: PlaybackStatus.Cordoned, serverName });
              });
              return [
                PlaybackMonitoringActions.getListForCleaningSuccess({ list: normalizedPlaybacks }),
              ];
            }),
            catchError(err => {
              const errMsg = this.utilsService.errMessage(err);
              return [
                SharedActions.consoleMessage({ error: errMsg }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public startCleanPlaybackList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlaybackMonitoringActions.startCleanPlaybackList),
      switchMap(() => {
        return this.playbackMonitoringService
          .cleanPlaybackList()
          .pipe(
            switchMap(() => {
              return [
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.CLEAN_PLAYBACK_LIST_SUCCESS') }),
                PlaybackMonitoringActions.cleanPlaybackListSuccess(),
              ];
            }),
            catchError(err => {
              const errMsg = this.utilsService.errMessage(err);
              return [
                SharedActions.showMessage({ error: errMsg }),
                SharedActions.consoleMessage({ error: errMsg }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public skip$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlaybackMonitoringActions.skip),
      switchMap(({ vmId, skip }) => {
        return this.playbackMonitoringService
          .skipPlayback(vmId, skip)
          .pipe(
            switchMap((res) => {
              return [
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.PLAYBACK_SKIP_SUCCESS') }),
                PlaybackMonitoringActions.skipSuccess({ vmId, skip }),
              ];
            }),
            catchError(err => {
              const errMsg = this.utilsService.errMessage(err);
              return [
                SharedActions.showMessage({ error: errMsg }),
                SharedActions.consoleMessage({ error: errMsg }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  constructor(private playbackMonitoringService: PlaybackMonitoringService,
              private actions$: Actions,
              private utilsService: UtilsService,
              private translateService: TranslateService) {

  }

  private generatePlaybackDevices(playback: PlaybackMonitoringModels.Playback) {
    const playbackDevices: PlaybackMonitoringModels.PlaybackDevice[] = [];

    playback.edge?.clients.QUIC?.forEach(device => {
      const playbackDevice: PlaybackMonitoringModels.PlaybackDevice = {
        name: playback.db.vmId,
        protocol: PlaybackProtocol.QUIC,
        state: PlaybackStatus.Active,
        status: playback.gcp.status,
        version: playback.db.version,
        zone: playback.db.zone,
        deviceId: device.Id,
      };
      playbackDevices.push(playbackDevice);
    });

    playback.edge?.clients.WebSocket?.forEach(device => {
      const playbackDevice: PlaybackMonitoringModels.PlaybackDevice = {
        name: playback.db.vmId,
        protocol: PlaybackProtocol.WebSocket,
        state: PlaybackStatus.Active,
        status: playback.gcp.status,
        version: playback.db.version,
        zone: playback.db.zone,
        deviceId: device.Id,
      };
      playbackDevices.push(playbackDevice);
    });

    return playbackDevices;
  }
}
